/**
 * Home tab: the emotional and clinical heart of the app.
 *
 * Prenatal phase: due-date countdown, prep checklist, and the "Baby is
 * here!" moment. Baby phase: congratulations, newborn checkup booking
 * (the app's number one job), the visit timeline, and today at a glance.
 * Role aware: partners see support tips and partner-specific copy.
 */
import { useEffect, useId, useState } from 'react';
import { useNav } from '../App';
import InstallCard from '../components/InstallCard';
import PersistCard from '../components/PersistCard';
import { Icons, ScreenHeader, Sheet } from '../components/ui';
import { useLocale, useT } from '../i18n/i18n';
import { OFFICE_PHONE_TEL, SCHEDULE_URL } from '../lib/config';
import { expectationsFor } from '../lib/expectations';
import { showCongrats, visitTimeline, type VisitInfo, type VisitStatus } from '../lib/reminders';
import { useActions, useApp } from '../lib/store';
import type { SyncStatus } from '../lib/sync';
import {
  dayOfLife,
  daysUntil,
  elapsedParts,
  formatDate,
  localDateOf,
  minutesSince,
  todayLocalDate,
  toLocalIso,
} from '../lib/time';
import type { FeedEvent, Profile, Role } from '../lib/types';
import { lbOzToGrams } from '../lib/units';

const CONGRATS_KEY = 'congratsDismissed';
const CHECKLIST_KEY = 'prenatalChecklist';
/** Show the gentle feed nudge after this many minutes without a logged feed (days 1 to 14). */
const NUDGE_AFTER_MIN = 180;

export default function HomeScreen() {
  const { profile } = useApp();

  // A minute tick keeps "ago" labels, day counts, and nudges fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!profile || profile.phase === 'prenatal') return <PrenatalHome />;
  return <BabyHome profile={profile} />;
}

// ── Shared bits ───────────────────────────────────────────────────────────

function SyncPill({ status }: { status: SyncStatus }) {
  const t = useT();
  const tone: Record<SyncStatus, string> = {
    idle: 'bg-good-soft text-good',
    syncing: 'bg-accent-soft text-accent',
    offline: 'bg-warn-soft text-warn',
    error: 'bg-bad-soft text-bad',
    unpaired: 'bg-surface-2 text-ink-faint',
  };
  return (
    <div className="flex justify-center">
      <span
        className={`rounded-full px-3 py-1 text-xs font-bold ${tone[status]} ${
          status === 'syncing' ? 'animate-pulse-soft' : ''
        }`}
      >
        {t.common.sync[status]}
      </span>
    </div>
  );
}

/** Inline "which date did you book?" picker used by both checkup cards. */
function BookedDatePicker({ onSave }: { onSave: (isoDate: string) => void }) {
  const t = useT();
  const id = useId();
  const [dateValue, setDateValue] = useState('');
  return (
    <div className="mt-3 rounded-2xl bg-surface-2 p-3">
      <label htmlFor={id} className="block text-sm font-bold text-ink-soft">
        {t.content.checkup.whenIsIt}
      </label>
      <div className="mt-2 flex gap-2">
        <input
          id={id}
          type="date"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          className="min-w-0 flex-1 rounded-xl border border-line bg-surface px-3 py-2.5 text-ink"
        />
        <button
          type="button"
          disabled={!dateValue}
          onClick={() => onSave(dateValue)}
          className="btn-primary shrink-0 px-5 py-2.5 disabled:opacity-50"
        >
          {t.common.actions.save}
        </button>
      </div>
    </div>
  );
}

// ── Prenatal phase ────────────────────────────────────────────────────────

function readChecklist(): number[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(CHECKLIST_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is number => typeof v === 'number') : [];
  } catch {
    return [];
  }
}

function PrenatalHome() {
  const { device, profile, pairing, syncStatus } = useApp();
  const t = useT();
  const locale = useLocale();
  const nav = useNav();
  const role: Role = device?.role ?? 'mom';
  const [sheetOpen, setSheetOpen] = useState(false);

  const days = profile?.dueDate ? daysUntil(profile.dueDate) : null;

  return (
    <div>
      <ScreenHeader
        title={t.common.appName}
        subtitle={t.content.prenatal.title}
        onSettings={() => nav.go('settings')}
      />
      <div className="space-y-4 px-4 py-4">
        {pairing ? <SyncPill status={syncStatus} /> : null}

        <InstallCard />
        <PersistCard />

        <section className="card relative overflow-hidden p-5 text-center">
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary-soft via-transparent to-accent-soft"
            aria-hidden="true"
          />
          <div className="relative">
            <p className="text-sm leading-relaxed text-ink-soft">{t.content.prenatal.intro}</p>
            {profile?.dueDate ? (
              <>
                <h2 className="mt-3 text-2xl text-primary">
                  {days !== null && days > 0
                    ? t.content.prenatal.countdown(days)
                    : t.content.prenatal.pastDue}
                </h2>
                <p className="mt-1 text-sm text-ink-faint">
                  {t.home.prenatal.dueDateLabel(formatDate(profile.dueDate, locale))}
                </p>
              </>
            ) : (
              <h2 className="mt-3 text-2xl text-primary">{t.home.prenatal.noDueDate}</h2>
            )}
          </div>
        </section>

        {role === 'mom' ? <PrenatalChecklistCard /> : <PartnerIntroCard />}

        {/* Disabled until a profile exists locally: a partner device that
            has not yet synced would otherwise author a sparse profile that
            clobbers mom's via last-writer-wins. */}
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          disabled={!profile}
          className="btn-primary w-full py-4 text-lg disabled:opacity-50"
        >
          {t.content.prenatal.babyIsHere}
        </button>
      </div>

      <BirthSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  );
}

function PrenatalChecklistCard() {
  const t = useT();
  const [checked, setChecked] = useState<number[]>(readChecklist);

  const toggle = (i: number) => {
    setChecked((prev) => {
      const next = prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i];
      try {
        localStorage.setItem(CHECKLIST_KEY, JSON.stringify(next));
      } catch {
        // Private-mode storage failures just lose persistence, never the UI.
      }
      return next;
    });
  };

  return (
    <section className="card p-5">
      <h2 className="text-base">{t.home.prenatal.checklistTitle}</h2>
      <ul className="mt-1">
        {t.content.prenatal.checklist.map((item, i) => {
          const done = checked.includes(i);
          return (
            <li key={i}>
              <label className="flex min-h-11 cursor-pointer items-start gap-3 py-2">
                <input
                  type="checkbox"
                  checked={done}
                  onChange={() => toggle(i)}
                  className="mt-0.5 h-5 w-5 shrink-0"
                  style={{ accentColor: 'var(--primary)' }}
                />
                <span
                  className={`text-sm leading-relaxed ${
                    done ? 'text-ink-faint line-through' : 'text-ink'
                  }`}
                >
                  {item}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 rounded-2xl bg-warn-soft p-3">
        <p className="text-xs leading-relaxed text-warn">{t.content.prenatal.colostrumNote}</p>
      </div>
    </section>
  );
}

function PartnerIntroCard() {
  const t = useT();
  const nav = useNav();
  return (
    <section className="card p-5">
      <h2 className="text-base">{t.content.partnerTips.title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-ink-soft">{t.content.partnerTips.intro}</p>
      <button
        type="button"
        onClick={() => nav.go('guide')}
        className="mt-2 flex min-h-11 items-center gap-1 text-sm font-bold text-primary"
      >
        {t.home.partner.guideLink}
        {Icons.chevronRight('h-4 w-4')}
      </button>
    </section>
  );
}

function BirthSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { settings } = useApp();
  const { updateProfile } = useActions();
  const t = useT();
  const whenId = useId();
  const nickId = useId();
  const [birthValue, setBirthValue] = useState(() => toLocalIso(new Date()).slice(0, 16));
  const [nickname, setNickname] = useState('');
  const [lb, setLb] = useState('');
  const [oz, setOz] = useState('');
  const [grams, setGrams] = useState('');
  const [saving, setSaving] = useState(false);

  // Default the birth time to "now" each time the sheet opens.
  useEffect(() => {
    if (open) setBirthValue(toLocalIso(new Date()).slice(0, 16));
  }, [open]);

  const save = async () => {
    if (!birthValue || saving) return;
    setSaving(true);
    try {
      const patch: Partial<Omit<Profile, 'updatedAt'>> = {
        phase: 'baby',
        birthAt: toLocalIso(new Date(birthValue)),
      };
      const name = nickname.trim();
      if (name) patch.babyNickname = name;
      const g =
        settings.weightUnit === 'g'
          ? Number(grams)
          : lbOzToGrams(Number(lb) || 0, Number(oz) || 0);
      if (Number.isFinite(g) && g > 0) patch.birthWeightG = Math.round(g);
      await updateProfile(patch);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full rounded-2xl border border-line bg-surface-2 px-4 py-3 text-ink';

  return (
    <Sheet open={open} onClose={onClose} title={t.content.prenatal.babyIsHere}>
      <div className="space-y-4">
        <div>
          <label htmlFor={whenId} className="mb-1 block text-sm font-bold text-ink-soft">
            {t.home.birthSheet.when}
          </label>
          <input
            id={whenId}
            type="datetime-local"
            value={birthValue}
            max={toLocalIso(new Date()).slice(0, 16)}
            onChange={(e) => setBirthValue(e.target.value)}
            className={inputCls}
          />
        </div>

        <div>
          <label htmlFor={nickId} className="mb-1 block text-sm font-bold text-ink-soft">
            {t.home.birthSheet.nickname}
          </label>
          <input
            id={nickId}
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            className={inputCls}
          />
          <p className="mt-1 text-xs text-ink-faint">{t.home.birthSheet.nicknameHint}</p>
        </div>

        <div>
          <p className="mb-1 text-sm font-bold text-ink-soft">{t.home.birthSheet.weight}</p>
          {settings.weightUnit === 'g' ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={grams}
                onChange={(e) => setGrams(e.target.value)}
                aria-label={t.home.birthSheet.grams}
                className="w-32 rounded-2xl border border-line bg-surface-2 px-4 py-3 text-ink"
              />
              <span className="text-sm text-ink-soft">{t.home.birthSheet.grams}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={lb}
                onChange={(e) => setLb(e.target.value)}
                aria-label={t.home.birthSheet.lb}
                className="w-20 rounded-2xl border border-line bg-surface-2 px-3 py-3 text-ink"
              />
              <span className="text-sm text-ink-soft">{t.home.birthSheet.lb}</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                max="15"
                value={oz}
                onChange={(e) => setOz(e.target.value)}
                aria-label={t.home.birthSheet.oz}
                className="w-20 rounded-2xl border border-line bg-surface-2 px-3 py-3 text-ink"
              />
              <span className="text-sm text-ink-soft">{t.home.birthSheet.oz}</span>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => void save()}
          disabled={!birthValue || saving}
          className="btn-primary w-full py-4 text-lg disabled:opacity-60"
        >
          {t.common.actions.save}
        </button>
      </div>
    </Sheet>
  );
}

// ── Baby phase ────────────────────────────────────────────────────────────

function BabyHome({ profile }: { profile: Profile }) {
  const { device, events, pairing, syncStatus } = useApp();
  const t = useT();
  const nav = useNav();
  const role: Role = device?.role ?? 'mom';

  // Clamp to Day 1: a birth date accidentally set in the future would
  // otherwise render "Day 0" or negative days.
  const day = Math.max(1, dayOfLife(profile.birthAt) ?? 1);
  const visits = visitTimeline(profile);
  const newborn = visits.find((v) => v.key === 'newborn');
  const lactation = visits.find((v) => v.key === 'lactation');

  const [congratsDismissed, setCongratsDismissed] = useState(
    () => localStorage.getItem(CONGRATS_KEY) === '1',
  );
  const dismissCongrats = () => {
    try {
      localStorage.setItem(CONGRATS_KEY, '1');
    } catch {
      // Storage failure only loses persistence of the dismissal.
    }
    setCongratsDismissed(true);
  };

  const today = todayLocalDate();
  const todaysEvents = events.filter((e) => !e.deleted && localDateOf(e.at) === today);
  const feedsToday = todaysEvents.filter((e) => e.type === 'feed').length;
  const wetToday = todaysEvents.filter((e) => e.type === 'diaper' && e.wet).length;
  const dirtyToday = todaysEvents.filter((e) => e.type === 'diaper' && e.dirty).length;
  const lastFeed = events.find((e): e is FeedEvent => e.type === 'feed' && !e.deleted);
  // "Since last feeding" is anchored to the feed's END (matching the Track
  // tab), so a long timer feed does not fire the nudge 40 minutes early.
  const lastFeedAnchor = lastFeed ? (lastFeed.endAt ?? lastFeed.at) : undefined;

  const showNudge =
    day >= 1 &&
    day <= 14 &&
    (!lastFeedAnchor || minutesSince(lastFeedAnchor) >= NUDGE_AFTER_MIN);

  return (
    <div>
      <ScreenHeader
        title={
          profile.babyNickname
            ? t.home.greetingWithName(profile.babyNickname)
            : t.home.greeting
        }
        subtitle={t.common.time.dayOfLife(day)}
        onSettings={() => nav.go('settings')}
      />
      <div className="space-y-4 px-4 py-4">
        {pairing ? <SyncPill status={syncStatus} /> : null}

        <InstallCard />
        <PersistCard />

        {showCongrats(profile, congratsDismissed) ? (
          <CongratsCard role={role} name={profile.babyNickname} onDismiss={dismissCongrats} />
        ) : null}

        {newborn ? <NewbornCheckupSection visit={newborn} role={role} profile={profile} /> : null}
        {lactation?.status === 'due' ? <LactationDueCard /> : null}

        {role === 'partner' ? <PartnerSupportCard day={day} /> : null}
        {role === 'partner' && day >= 3 && day <= 21 ? (
          <section className="flex items-start gap-3 rounded-3xl bg-primary-soft p-4">
            <span className="mt-0.5 shrink-0 text-primary">{Icons.heart('h-5 w-5')}</span>
            <p className="text-sm leading-relaxed text-ink-soft">{t.content.mood.partnerNote}</p>
          </section>
        ) : null}

        {showNudge ? <NudgeCard hasFeeds={Boolean(lastFeed)} /> : null}

        <TodayCard
          day={day}
          feeds={feedsToday}
          wets={wetToday}
          dirties={dirtyToday}
          lastFeedAt={lastFeedAnchor}
          hasEventsToday={todaysEvents.length > 0}
        />

        <VisitTimelineCard visits={visits} />

        <p className="px-2 pb-2 text-center text-xs leading-relaxed text-ink-faint">
          {t.common.disclaimer}
        </p>
      </div>
    </div>
  );
}

function CongratsCard({
  role,
  name,
  onDismiss,
}: {
  role: Role;
  name?: string;
  onDismiss: () => void;
}) {
  const t = useT();
  return (
    <section className="card relative overflow-hidden p-5">
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary-soft via-transparent to-accent-soft"
        aria-hidden="true"
      />
      <span className="pointer-events-none absolute right-12 top-3 rotate-12 text-2xl opacity-80" aria-hidden="true">
        🎉
      </span>
      {/* Drawn rather than the 💜 emoji so it follows the chosen accent. */}
      <svg
        viewBox="0 0 24 24"
        className="pointer-events-none absolute bottom-8 right-14 h-5 w-5 text-primary opacity-70"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M12 20.5S4 15.3 4 9.8A4.3 4.3 0 0 1 8.3 5.5c1.6 0 3 .9 3.7 2.2a4.2 4.2 0 0 1 3.7-2.2A4.3 4.3 0 0 1 20 9.8c0 5.5-8 10.7-8 10.7Z" />
      </svg>
      <span className="pointer-events-none absolute bottom-3 right-4 text-3xl opacity-90" aria-hidden="true">
        👶
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t.common.actions.close}
        className="absolute right-2 top-2 z-10 rounded-full p-2 text-ink-soft hover:bg-surface-2"
      >
        {Icons.x('h-5 w-5')}
      </button>
      <div className="relative pr-16">
        <h2 className="text-xl leading-snug">
          {name ? t.content.congrats.titleWithName(name) : t.content.congrats.title}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-ink-soft">
          {role === 'partner' ? t.content.congrats.partnerBody : t.content.congrats.body}
        </p>
      </div>
    </section>
  );
}

function NewbornCheckupSection({
  visit,
  role,
  profile,
}: {
  visit: VisitInfo;
  role: Role;
  profile: Profile;
}) {
  const t = useT();
  const locale = useLocale();
  const { updateProfile } = useActions();
  const [showPicker, setShowPicker] = useState(false);

  if (visit.status === 'scheduled' && profile.newbornCheckupAt) {
    const d = daysUntil(profile.newbornCheckupAt);
    return (
      <section className="card flex items-start gap-3 p-4">
        <span className="mt-0.5 shrink-0 text-good">{Icons.calendar('h-6 w-6')}</span>
        <div className="min-w-0">
          <h2 className="text-base">{t.content.visits.newborn.name}</h2>
          <p className="mt-0.5 text-sm font-bold text-good">
            {t.content.checkup.scheduledFor(formatDate(profile.newbornCheckupAt, locale))}
          </p>
          {d >= 0 ? <p className="text-sm text-ink-soft">{t.home.checkup.inDays(d)}</p> : null}
        </div>
      </section>
    );
  }

  if (visit.status !== 'due') return null;

  return (
    <section className="card p-5">
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-warn-soft px-2.5 py-1 text-xs font-bold text-warn">
          {t.home.visits.status.due}
        </span>
        <span className="text-xs text-ink-faint">{t.content.visits.newborn.window}</span>
      </div>
      <h2 className="mt-2 text-lg leading-snug">{t.content.checkup.title}</h2>
      <p className="mt-1 text-sm leading-relaxed text-ink-soft">
        {role === 'partner' ? t.content.checkup.partnerBody : t.content.checkup.body}
      </p>
      <div className="mt-4 grid gap-2">
        <a
          href={SCHEDULE_URL}
          target="_blank"
          rel="noopener"
          className="btn-primary block px-4 py-3.5 text-center"
        >
          {t.content.checkup.scheduleOnline}
        </a>
        <div className="grid grid-cols-2 gap-2">
          <a
            href={`tel:${OFFICE_PHONE_TEL}`}
            className="btn-secondary flex items-center justify-center gap-1.5 px-3 py-3 text-center text-sm"
          >
            {Icons.phone('h-4 w-4')}
            {t.content.checkup.callToSchedule}
          </a>
          <button
            type="button"
            onClick={() => setShowPicker((v) => !v)}
            className="btn-secondary px-3 py-3 text-sm"
          >
            {t.content.checkup.markScheduled}
          </button>
        </div>
      </div>
      {showPicker ? (
        <BookedDatePicker onSave={(d) => void updateProfile({ newbornCheckupAt: d })} />
      ) : null}
    </section>
  );
}

function LactationDueCard() {
  const t = useT();
  const { updateProfile } = useActions();
  const [showPicker, setShowPicker] = useState(false);
  return (
    <section className="card p-4">
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-accent-soft px-2.5 py-1 text-xs font-bold text-accent">
          {t.home.visits.status.due}
        </span>
        <span className="text-xs text-ink-faint">{t.content.visits.lactation.window}</span>
      </div>
      <h2 className="mt-2 text-base">{t.content.visits.lactation.name}</h2>
      <p className="mt-1 text-sm leading-relaxed text-ink-soft">{t.content.visits.lactation.desc}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <a
          href={SCHEDULE_URL}
          target="_blank"
          rel="noopener"
          className="btn-secondary px-4 py-2.5 text-sm"
        >
          {t.content.checkup.scheduleOnline}
        </a>
        <button
          type="button"
          onClick={() => setShowPicker((v) => !v)}
          className="btn-secondary px-4 py-2.5 text-sm"
        >
          {t.content.checkup.markScheduled}
        </button>
      </div>
      {showPicker ? (
        <BookedDatePicker onSave={(d) => void updateProfile({ lactationFollowupAt: d })} />
      ) : null}
    </section>
  );
}

function PartnerSupportCard({ day }: { day: number }) {
  const t = useT();
  const nav = useNav();
  const tips = t.content.partnerTips.tips;
  const tip = tips[(Math.max(1, day) - 1) % tips.length];
  return (
    <section className="card p-5">
      <h2 className="text-base">{t.content.partnerTips.title}</h2>
      <div className="mt-2 rounded-2xl bg-primary-soft p-3">
        <p className="text-sm font-bold text-primary">{tip.title}</p>
        <p className="mt-0.5 text-sm leading-relaxed text-ink-soft">{tip.body}</p>
      </div>
      <button
        type="button"
        onClick={() => nav.go('guide')}
        className="mt-1 flex min-h-11 items-center gap-1 text-sm font-bold text-primary"
      >
        {t.home.partner.guideLink}
        {Icons.chevronRight('h-4 w-4')}
      </button>
    </section>
  );
}

function NudgeCard({ hasFeeds }: { hasFeeds: boolean }) {
  const t = useT();
  const nav = useNav();
  return (
    <section className="card flex items-center gap-3 p-4">
      <p className="min-w-0 flex-1 text-sm leading-relaxed text-ink-soft">
        {hasFeeds ? t.home.nudge.sinceLastFeed : t.home.nudge.noFeedsYet}
      </p>
      <button
        type="button"
        onClick={() => nav.go('track')}
        className="btn-secondary shrink-0 px-4 py-2.5 text-sm"
      >
        {t.home.nudge.action}
      </button>
    </section>
  );
}

function MeterRow({
  label,
  count,
  min,
  sub,
}: {
  label: string;
  count: number;
  min: number;
  sub?: string;
}) {
  const t = useT();
  const met = count >= min;
  const segments = Math.max(1, min);
  return (
    <div className="py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-ink">{label}</p>
          {sub ? <p className="text-xs text-ink-faint">{sub}</p> : null}
        </div>
        <p className={`shrink-0 text-sm font-bold ${met ? 'text-good' : 'text-ink-soft'}`}>
          {t.home.today.ofMin(count, min)}
        </p>
      </div>
      <div className="mt-1.5 flex items-center gap-1" aria-hidden="true">
        {Array.from({ length: segments }, (_, i) => (
          <span
            key={i}
            className={`h-2 flex-1 rounded-full ${
              i < Math.min(count, segments) ? 'bg-primary' : 'bg-surface-2'
            }`}
          />
        ))}
        {count > segments ? (
          <span className="shrink-0 text-xs font-bold text-good">+{count - segments}</span>
        ) : null}
      </div>
    </div>
  );
}

function TodayCard({
  day,
  feeds,
  wets,
  dirties,
  lastFeedAt,
  hasEventsToday,
}: {
  day: number;
  feeds: number;
  wets: number;
  dirties: number;
  lastFeedAt?: string;
  hasEventsToday: boolean;
}) {
  const t = useT();
  const exp = expectationsFor(day);
  const [wakeDismissed, setWakeDismissed] = useState(false);

  let lastFeedSub: string | undefined;
  if (lastFeedAt) {
    const { hours, minutes } = elapsedParts(lastFeedAt);
    lastFeedSub = t.home.today.lastFeed(
      hours === 0 && minutes === 0 ? t.common.time.justNow : t.common.time.agoHM(hours, minutes),
    );
  }

  return (
    <section className="card p-5">
      <h2 className="text-base">{t.home.today.title}</h2>
      {!hasEventsToday ? (
        <p className="mt-1 text-xs leading-relaxed text-ink-faint">{t.home.today.empty}</p>
      ) : null}

      <div className="mt-2 divide-y divide-line">
        <MeterRow label={t.home.today.feeds} count={feeds} min={exp.feedsMin} sub={lastFeedSub} />
        <MeterRow label={t.home.today.wetDiapers} count={wets} min={exp.wetMin} />
        <MeterRow label={t.home.today.dirtyDiapers} count={dirties} min={exp.dirtyMin} />
      </div>

      <div className="mt-3 rounded-2xl bg-accent-soft p-3">
        <p className="text-sm font-bold text-accent">{t.content.stages[exp.stageKey].name}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-ink-soft">
          {t.content.stages[exp.stageKey].desc}
        </p>
      </div>

      {day <= 14 && !wakeDismissed ? (
        <div className="mt-3 flex items-start gap-2 rounded-2xl bg-warn-soft p-3">
          <p className="min-w-0 flex-1 text-xs leading-relaxed text-warn">
            {t.content.expectations.wakeToFeed}
          </p>
          <button
            type="button"
            onClick={() => setWakeDismissed(true)}
            aria-label={t.common.actions.close}
            className="-m-1 shrink-0 rounded-full p-2 text-warn"
          >
            {Icons.x('h-4 w-4')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function VisitTimelineCard({ visits }: { visits: VisitInfo[] }) {
  const t = useT();
  const locale = useLocale();
  const chipTone: Record<VisitStatus, string> = {
    upcoming: 'bg-surface-2 text-ink-soft',
    due: 'bg-warn-soft text-warn',
    scheduled: 'bg-good-soft text-good',
    past: 'bg-surface-2 text-ink-faint',
  };
  return (
    <section className="card p-5">
      <h2 className="text-base">{t.home.visits.title}</h2>
      <ul className="mt-1 divide-y divide-line">
        {visits.map((v) => (
          <li key={v.key} className="flex items-center justify-between gap-3 py-3 last:pb-0">
            <div className="min-w-0">
              <p
                className={`text-sm font-bold ${
                  v.status === 'past' ? 'text-ink-faint' : 'text-ink'
                }`}
              >
                {t.content.visits[v.key].name}
              </p>
              <p className="text-xs text-ink-faint">
                {v.scheduledFor
                  ? t.content.checkup.scheduledFor(formatDate(v.scheduledFor, locale))
                  : t.content.visits[v.key].window}
              </p>
            </div>
            <span
              className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${chipTone[v.status]}`}
            >
              {t.home.visits.status[v.status]}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
